// FullPageCalendar.js
import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Clock, MapPin, Users, Star, AlertCircle } from 'lucide-react';
import BackendTemplateConfig from '@/configs/BackendTemplateConfig';

const FullPageCalendar = () => {
  const config = BackendTemplateConfig;
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [events, setEvents] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Event types for the glossary
  const eventTypes = [
    { type: 'meeting', color: config.eventTypes.meeting, label: 'Meeting', icon: Users },
    { type: 'deadline', color: config.eventTypes.deadline, label: 'Deadline', icon: AlertCircle },
    { type: 'personal', color: config.eventTypes.personal, label: 'Personal', icon: Star },
    { type: 'holiday', color: config.eventTypes.holiday, label: 'Holiday', icon: Calendar },
  ];

  // Mock API function - replace with your actual API endpoint
  const fetchMonthData = async (year, month) => {
    setLoading(true);
    setError(null);
    
    try {
      // Simulate API call - replace with actual fetch
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Mock data - replace with actual API response
      const mockEvents = {
        [`${year}-${String(month + 1).padStart(2, '0')}-05`]: [
          { id: 1, title: 'Team Standup', type: 'meeting', time: '09:00', location: 'Conference Room A' },
          { id: 2, title: 'Project Deadline', type: 'deadline', time: '17:00', location: 'Online' }
        ],
        [`${year}-${String(month + 1).padStart(2, '0')}-15`]: [
          { id: 3, title: 'Birthday Party', type: 'personal', time: '19:00', location: "Sarah's House" }
        ],
        [`${year}-${String(month + 1).padStart(2, '0')}-25`]: [
          { id: 4, title: 'Christmas', type: 'holiday', time: 'All Day', location: 'Worldwide' }
        ],
      };
      
      setEvents(mockEvents);
    } catch (err) {
      setError('Failed to load events. Please try again.');
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when month changes
  useEffect(() => {
    fetchMonthData(currentDate.getFullYear(), currentDate.getMonth());
  }, [currentDate.getMonth(), currentDate.getFullYear()]);

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDateKey = (year, month, day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const handleDateClick = (day) => {
    const dateKey = formatDateKey(currentDate.getFullYear(), currentDate.getMonth(), day);
    setSelectedDate(dateKey);
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className={config.calendar.dayCell.empty}></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = formatDateKey(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dayEvents = events[dateKey] || [];
      const isSelected = selectedDate === dateKey;
      const isToday = new Date().toDateString() === new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();

      days.push(
        <div
          key={day}
          onClick={() => handleDateClick(day)}
          className={`${config.calendar.dayCell.base} ${
            isSelected ? config.calendar.dayCell.selected : ''
          } ${isToday ? config.calendar.dayCell.today : ''}`}
        >
          <div className={isToday ? config.calendar.dayNumber.today : config.calendar.dayNumber.default}>
            {day}
          </div>
          <div className={config.card.spacing}>
            {dayEvents.slice(0, 2).map((event) => {
              const eventType = eventTypes.find(t => t.type === event.type);
              return (
                <div
                  key={event.id}
                  className={`${config.calendar.event.chip} ${eventType.color}`}
                >
                  {event.title}
                </div>
              );
            })}
            {dayEvents.length > 2 && (
              <div className={config.calendar.event.more}>+{dayEvents.length - 2} more</div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  const selectedEvents = selectedDate ? events[selectedDate] || [] : [];

  return (
    <div className={`${config.container.minHeight} ${config.container.backgroundGradient} ${config.container.padding}`}>
      <div className={`${config.container.maxWidth} ${config.container.marginX}`}>
        <div className={config.calendar.wrapper}>
          <div className={`${config.layout.flex} ${config.responsive.stackMobile} h-full`}>
            {/* Main Calendar Section */}
            <div className={config.calendar.mainSection}>
              {/* Header */}
              <div className={config.calendar.headerRow}>
                <h1 className={`${config.typography.pageTitle} ${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.medium}`}>
                  <Calendar className={`${config.icons.large} ${config.colors.primary.text}`} />
                  Calendar
                </h1>
                <div className={config.calendar.monthSelector}>
                  <button
                    onClick={previousMonth}
                    className={config.buttons.ghost}
                  >
                    <ChevronLeft className={config.icons.normal} />
                  </button>
                  <h2 className={config.calendar.monthLabel}>
                    {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </h2>
                  <button
                    onClick={nextMonth}
                    className={config.buttons.ghost}
                  >
                    <ChevronRight className={config.icons.normal} />
                  </button>
                </div>
                <button
                  onClick={() => fetchMonthData(currentDate.getFullYear(), currentDate.getMonth())}
                  className={config.buttons.primary}
                >
                  Refresh
                </button>
              </div>

              {/* Loading/Error States */}
              {loading && (
                <div className={config.states.loading}>Loading events...</div>
              )}
              {error && (
                <div className={config.states.error}>{error}</div>
              )}

              {/* Days of Week Header */}
              <div className={`grid grid-cols-7 ${config.spacing.none} mb-2`}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className={config.calendar.weekDayHeader}>
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className={config.calendar.grid}>
                {renderCalendarDays()}
              </div>
            </div>

            {/* Info Section */}
            <div className={config.calendar.sidePanel}>
              <h3 className={`${config.typography.sectionTitle} mb-4`}>Event Details</h3>
              
              {selectedDate ? (
                <div>
                  <div className={`${config.typography.body} mb-3`}>
                    {new Date(selectedDate).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                  
                  {selectedEvents.length > 0 ? (
                    <div className={config.card.spacingLarge}>
                      {selectedEvents.map(event => {
                        const eventType = eventTypes.find(t => t.type === event.type);
                        return (
                          <div key={event.id} className={`${config.card.simple} ${config.card.padding}`}>
                            <div className={`${config.layout.flexStart} ${config.layout.flexGap.large}`}>
                              <div className={`p-2 ${config.borderRadius.md} ${eventType.color}`}>
                                <eventType.icon className={`${config.icons.medium} text-white`} />
                              </div>
                              <div className="flex-1">
                                <h4 className={config.typography.heading}>{event.title}</h4>
                                <div className={`${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.small} ${config.typography.body} mt-1`}>
                                  <Clock className={config.icons.small} />
                                  <span>{event.time}</span>
                                </div>
                                <div className={`${config.layout.flex} ${config.layout.flexCenter} ${config.layout.flexGap.small} ${config.typography.body} mt-1`}>
                                  <MapPin className={config.icons.small} />
                                  <span>{event.location}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className={config.calendar.emptyState}>
                      No events scheduled for this day
                    </div>
                  )}
                </div>
              ) : (
                <div className={config.calendar.emptyState}>
                  Select a date to view events
                </div>
              )}

              {/* Quick Stats */}
              <div className={config.calendar.statsSection}>
                <h4 className={`${config.typography.label} mb-3`}>This Month</h4>
                <div className={config.card.spacing}>
                  <div className={`${config.layout.flexBetween} text-sm`}>
                    <span className={config.typography.body}>Total Events</span>
                    <span className="font-semibold">
                      {Object.values(events).reduce((acc, dayEvents) => acc + dayEvents.length, 0)}
                    </span>
                  </div>
                  <div className={`${config.layout.flexBetween} text-sm`}>
                    <span className={config.typography.body}>Days with Events</span>
                    <span className="font-semibold">{Object.keys(events).length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Glossary Section */}
          <div className={config.calendar.glossary.wrapper}>
            <h3 className={`${config.typography.label} mb-3`}>Event Types</h3>
            <div className={`${config.layout.flex} flex-wrap ${config.layout.flexGap.xlarge}`}>
              {eventTypes.map(type => (
                <div key={type.type} className={config.calendar.glossary.item}>
                  <div className={`${config.calendar.glossary.indicator} ${type.color}`}></div>
                  <span className={config.typography.body}>{type.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullPageCalendar;